<template>
  <div
    class="bicycle-plugins-container"
    :class="{ 'map-fullscreen': isMapFullscreen }"
  >
    <LeftJoinBicycle
      ref="leftJoin"
      :vehicle-data-autocomplete="true"
      :require-route-fields="false"
      :show-garage-panel="true"
      :default-vehicle-mode="defaultVehicleMode"
      :garage-tree-list-use-request="true"
      :garage-tree-children-use-request="true"
      @toggle="handleToggle"
      @search="handleSearch"
      @vehicle-mode-change="onVehicleModeChange"
      @plate-color-options-change="plateColorState = $event"
      @plate-color-account-change="clearPositionAccount"
      @history-locate-on-map="handleHistoryLocateOnMap"
      @result-locate-on-map="handleResultLocateOnMap"
    />
    <RightDataBicycle
      ref="rightData"
      :isCollapsed="isCollapsed"
      :isExpandedNum="isExpandedNum"
      :run-route="bicycleRunRoute"
      @collapse="handleCollapse"
      @focus-row-on-map="handleFocusRightTableRow"
    />
    <div v-if="positionSession && positionSession.items.length" class="native-position-status">
      <el-button v-if="positionSession.running" size="mini" @click="positionSession.cancel()">取消批次</el-button>
      <div v-for="item in positionSession.items" :key="item.key">{{ item.vehicleNo }}（{{ plateColorLabel(item.plateColor) }}） {{ statusLabel(item.status) }} <span v-if="item.message">{{ item.message }}</span><el-button v-if="item.status === 'FAILED' && !item.retries" type="text" :disabled="positionSession.running" @click="positionSession.retry(item.key)">重试一次</el-button></div>
    </div>
    <div class="map-container">
      <div class="right-panel" v-show="!isMapFullscreen">
        <div class="top-controls">
          <div class="top-controls__map-tools">
            <div class="map-tool-item" @click="toggleSatelliteLayer">
              <img src="@/assets/weixing.png" alt="" class="control-btn-icon" />
              <el-button
                type="text"
                class="control-btn"

                >{{ showSatellite ? "普通" : "卫星" }}</el-button
              >
            </div>
            <div class="map-tool-item" @click="toggleRanging">
              <img src="@/assets/ceju.png" alt="" class="control-btn-icon" />
              <el-button type="text" class="control-btn"
                >测距</el-button
              >
            </div>
            <div class="map-tool-item" @click="zoomGlobal">
              <img src="@/assets/quanju.png" alt="" class="control-btn-icon" />
              <el-button type="text" class="control-btn"
                >全局</el-button
              >
            </div>
            <div class="map-tool-item" @click="toggleMapFullscreen">
              <img src="@/assets/quanping.png" alt="" class="control-btn-icon" />
              <el-button
                type="text"
                class="control-btn"
                >全屏</el-button
              >
            </div>
          </div>
          <div class="zoom-controls">
            <i
              style="color: #7c8196; font-size: 1.5rem"
              class="el-icon-plus"
              @click="zoomIn"
            ></i>
            <i
              style="color: #7c8196; font-size: 1.5rem"
              class="el-icon-minus"
              @click="zoomOut"
            ></i>
          </div>
        </div>
      </div>
      <div ref="bicycleMapEl" id="amap-bicycle" class="amap"></div>
      <div v-if="isMapFullscreen" class="fullscreen-tip">
        若要退出全屏模式，请按 <span class="esc-key">Esc</span>
      </div>
    </div>
  </div>
</template>

<script>
import LeftJoinBicycle from "@/components/LeftJoinBicycle.vue";
import RightDataBicycle from "@/components/RightDataBicycle.vue";
import { hasPermission } from '@/utils/auth';
import { nativePlateColorLabel } from '@/mixins/nativePlateColors';
import { createVehicleMonitoringSession } from '@/views/vehicle-monitor/vehicleMonitoringSession';
import { queryIdentities, identityKey } from '@/views/vehicle-monitor/nativeVehicleMonitoring';
import { queryNativePosition } from '@/views/vehicle-monitor/nativeVehicleMonitoringApi';
import { loadAmapSDK, setAmapSecurityConfig } from "@/utils/amapConfig";
import {
  destroyAmapMapInstance,
  refreshAmapMapDisplay,
} from "@/utils/amapMapKeepAlive";
import { tiandituReverseGeocode } from "@/utils/tiandituGeocoder";
import coordtransform from "@/WGS84.js";
import markerIconRed from "@/assets/red.png";
import markerIconHui from "@/assets/hui.png";
import markerIconLv from "@/assets/lv.png";
import {
  formatDirectionText,
} from "@/utils/vehicleHeading";
import {
  buildBicycleMarkerContent,
} from "@/utils/truckLastLocation";


const MARKER_UTC_STALE_MS = 15 * 60 * 1000;
/** 单车定位默认缩放 */
const FIT_SINGLE_VEHICLE_ZOOM = 14;
/** 多车 fit 后允许的最大级别，避免点很近时视野过近 */
const FIT_MULTI_MAX_ZOOM = 14;
/** 车标下方车牌/速度标签相对锚点的可视高度（px） */
const MARKER_LABEL_CLEARANCE_PX = 64;

/* global AMap */

function pad2(n) {
  return String(n).padStart(2, "0");
}

function lonLatToGcj02(lon, lat) {
  const nlon = Number(lon);
  const nlat = Number(lat);
  if (Number.isNaN(nlon) || Number.isNaN(nlat)) return [116.404, 39.915];
  if (Math.max(Math.abs(nlon), Math.abs(nlat)) > 10000) {
    const lonW = nlon / 600000.0;
    const latW = nlat / 600000.0;
    return coordtransform.wgs84togcj02(lonW, latW);
  }
  return [nlon, nlat];
}

function formatGtm(gtm) {
  if (!gtm || String(gtm).length < 15) return "—";
  try {
    const datePart = gtm.substring(0, 8);
    const timePart = gtm.substring(9);
    return `${datePart.substring(0, 4)}-${datePart.substring(
      4,
      6
    )}-${datePart.substring(6, 8)} ${timePart.substring(
      0,
      2
    )}:${timePart.substring(2, 4)}:${timePart.substring(4, 6)}`;
  } catch (e) {
    return "—";
  }
}

/** getTruckLastLocation 等接口 utc（秒或毫秒）→ 轨迹用 gtm 串 yyyymmdd/hhmmss */
function utcToGtmFromApi(utc) {
  if (utc == null || utc === "") return "";
  let ms = Number(utc);
  if (!Number.isFinite(ms)) return "";
  if (ms < 1e12) ms *= 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const h = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  const sec = pad2(d.getSeconds());
  return `${y}${m}${day}/${h}${min}${sec}`;
}

function parseSpd(item) {
  if (!item) return null;
  const raw = item.spd !== undefined ? item.spd : item.speed !== undefined ? item.speed : item.SPD;
  const n =
    Number(raw);
  return raw != null && raw !== '' && Number.isFinite(n) && n >= 0 ? n : null;
}

/** 归一化行上的 utc（毫秒），优先 raw.utc */
function utcMsFromLocationRow(row) {
  if (!row) return null;
  const raw = row.raw || {};
  const u = raw.utc != null && raw.utc !== "" ? raw.utc : row.utc;
  if (u == null || u === "") return null;
  let ms = Number(u);
  if (!Number.isFinite(ms)) return null;
  if (ms < 1e12) ms *= 1000;
  return ms;
}

/** 状态规则：>15分钟离线；<=15分钟且速度0停止；<=15分钟且速度>0在线 */
function getBicycleMarkerState(row) {
  if (!row) return "离线";
  const spd = parseSpd(row);
  const utcMs = utcMsFromLocationRow(row);
  if (utcMs == null || Date.now() - utcMs > MARKER_UTC_STALE_MS) return "离线";
  if (spd == null) return "未知";
  if (spd === 0) return "停止";
  return "在线";
}

/** 地图/表格展示：优先查询快照 sts（与左侧结果一致），无 sts 再按 utc+速度推算 */
function resolveBicycleDisplayState(row) {
  if (!row) return "离线";
  const sts =
    row.sts != null && String(row.sts).trim() !== ""
      ? String(row.sts).trim()
      : "";
  if (sts === "在线" || sts === "停止" || sts === "离线") return sts;
  return getBicycleMarkerState(row);
}

function pickBicycleMarkerIconUrl(row) {
  const s = resolveBicycleDisplayState(row);
  if (s === "离线" || s === "未知") return markerIconHui;
  if (s === "停止") return markerIconRed;
  return markerIconLv;
}

function formatBicycleTableStatus(row) {
  return resolveBicycleDisplayState(row);
}

function formatIwUpdateTime(row) {
  const g = row && row.gtm ? formatGtm(row.gtm) : "—";
  if (g && g !== "—") return g;
  const ms = utcMsFromLocationRow(row);
  if (ms == null) return "—";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate()
  )} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function formatIwMileageKm(row) {
  const raw =
    row && row.mil != null && row.mil !== ""
      ? row.mil
      : row && row.raw && row.raw.mil != null && row.raw.mil !== ""
      ? row.raw.mil
      : null;
  const n = raw != null ? Number(raw) : NaN;
  return Number.isFinite(n) ? `${n.toFixed(2)} km` : "—";
}

export default {
  name: "BicyclePluginsName",
  components: {
    LeftJoinBicycle,
    RightDataBicycle,
  },
  props: {
    defaultVehicleMode: {
      type: String,
      default: 'single',
      validator: (v) => v === 'single' || v === 'multi'
    }
  },
  data() {
    return {
      positionSession: null, positionSearchForm: {}, positionConditions: {},
      isCollapsed: false,
      isExpandedNum: 1,
      isMapFullscreen: false,
      map: null,
      ruler: null,
      isRanging: false,
      /** visualRoute 轨迹点，供右下表格 */
      bicycleRunRoute: [],
      /** 地图上展示的车标（接口多点 / 多车 / 单点） */
      bicycleMarkers: [],
      activeInfoWindow: null,
      lastRoutePayload: null,
      /** 与右侧表格状态规则一致：单车 / 多车 */
      lastQueryVehicleMode: this.defaultVehicleMode === 'multi' ? 'multi' : 'single',
      /** 查询完成后记录的全局视角，供「全局」按钮恢复 */
      plateColorState: { plateColorOptions: [], nativePlateColorLabels: {}, nativePlateColorsLoaded: false },
      lastGlobalFitView: null,
      /** 高德地图初始化 Promise，供查询前等待，避免「地图未就绪」竞态 */
      mapInitPromise: null,
      /** initMap 已失败时不再重复提示「地图未就绪」 */
      mapInitFailed: false,
      showSatellite: false,
      tileLayer: {
        satellite: null,
        roadNet: null,
      },
    };
  },
  watch: {
    'positionSession.items': { deep: true, handler() { this.syncPositionRows() } },
    isCollapsed() {
      this.$nextTick(() => {
        try {
          if (this.map) this.map.resize();
        } catch (e) {
          /* ignore */
        }
      });
    },
  },
  activated() {
    this.$nextTick(() => {
      this.restoreBicycleMapAfterTabSwitch();
    });
  },
  deactivated() {
    this.positionSession.cancel();
    this.releaseBicycleMapForTabSwitch();
  },
  created() { this.positionSession = createVehicleMonitoringSession(item => queryNativePosition(item, this.positionConditions)) },
  mounted() {
    setAmapSecurityConfig();
    this.mapInitPromise = this.initMap();
    window.addEventListener("keydown", this.onKeydown);
    this.mapInitPromise.catch(() => {});
    // 默认多车或 LeftJoin immediate 发出的 mode-change 早于 $refs：挂载后再拉一次
    this.$nextTick(() => {
      const lj = this.$refs.leftJoin;
      if (lj && lj.vehicleMode === "multi") {
        this.bootstrapMultiVehicleLocate();
      }
    });
  },
  beforeDestroy() {
    this.positionSession.cancel();
    window.removeEventListener("keydown", this.onKeydown);
    this.releaseBicycleMapForTabSwitch();
  },
  methods: {
    clearPositionAccount() { this.positionSession.cancel(); this.positionSession.items = []; this.positionSearchForm = {}; this.positionConditions = {}; this.syncPositionRows() },
    plateColorLabel(value) { return nativePlateColorLabel(this.plateColorState, value) },
    statusLabel(status) { return { PENDING: '待查询', LOADING: '查询中', SUCCESS: '成功', NO_DATA: '无数据', FAILED: '失败', CANCELED: '已取消' }[status] || status },
    syncPositionRows() {
      if (!this.positionSession) return;
      const rows = this.positionSession.items.filter(item => item.status === 'SUCCESS').map(item => this.normalizeLastLocationRow(item.result, item.plateColor));
      this.bicycleRunRoute = rows;
      const payload = { runRoute: rows, firstVcl: rows[0] || null, others: rows.slice(1), vehicleMode: this.lastQueryVehicleMode };
      if (this.$refs.rightData) this.$refs.rightData.updateRouteData(this.lastQueryVehicleMode === 'multi' ? { ...payload, firstVcl: null, others: rows } : payload);
      const left = this.$refs.leftJoin;
      if (left) { this.syncLeftJoinBicycleRouteSummary(payload, this.positionSearchForm); left.setMultiVehicleQueryResult(rows); left.mergeHistoryFromQueryRows(rows) }
      if (this.map) this.renderBicycleMarkers(rows, this.positionSearchForm);
    },
    releaseBicycleMapForTabSwitch() {
      this.clearBicycleMarkers();
      destroyAmapMapInstance(this.map);
      this.map = null;
      this.mapInitPromise = null;
    },
    async restoreBicycleMapAfterTabSwitch() {
      await this.$nextTick();
      if (!this.map) {
        this.mapInitPromise = null;
        try {
          await this.initMap();
        } catch (e) {
          return;
        }
      } else {
        await this.ensureMapReady();
      }
      if (!this.map) return;
      const el =
        (this.$refs && this.$refs.bicycleMapEl) ||
        document.getElementById("amap-bicycle");
      refreshAmapMapDisplay(this.map, null, { container: el });
      if (Array.isArray(this.bicycleRunRoute) && this.bicycleRunRoute.length) {
        this.renderBicycleMarkers(this.bicycleRunRoute);
        if (this.$refs.rightData && typeof this.$refs.rightData.updateRouteData === "function") {
          const rows = this.bicycleRunRoute;
          const vm = this.lastQueryVehicleMode === "multi" ? "multi" : "single";
          this.$refs.rightData.updateRouteData({
            runRoute: rows,
            firstVcl: rows[0] || null,
            others: rows.slice(1),
            vehicleMode: vm,
          });
        }
        this.syncLeftJoinMultiVehicleResultIfNeeded();
      }
    },
    onVehicleModeChange(mode) {
      if (this.positionSession) this.positionSession.cancel();
      if (mode === "multi") {
        this.bootstrapMultiVehicleLocate();
      }
    },
    /**
     * 进入多车：仅回填服务端已选车辆并同步车库勾选，不自动查询。
     */
    async bootstrapMultiVehicleLocate() {
      if (this._multiBootstrapInFlight) return;
      this._multiBootstrapInFlight = true;
      try {
        await this.$nextTick();
        const lj = this.$refs.leftJoin;
        if (!lj || lj.vehicleMode !== "multi") return;
        if (typeof lj.refreshSelectedTbVehiclesFromServer === "function") {
          await lj.refreshSelectedTbVehiclesFromServer({
            suppressTabSwitch: true,
          });
        }
        // 车库树可能仍在加载；已加载则立刻回显，未加载则等 tree-loaded
        if (typeof lj.restoreGarageCheckedFromSelected === "function") {
          await this.$nextTick();
          lj.restoreGarageCheckedFromSelected();
        } else {
          const plates = (lj.multiSelectedVehicles || [])
            .map((v) => (v && v.vclN != null ? String(v.vclN).trim() : ""))
            .filter(Boolean);
          if (
            plates.length &&
            typeof lj.syncGarageTreeCheckedPlates === "function"
          ) {
            lj.syncGarageTreeCheckedPlates(plates, true);
          }
        }
      } catch (e) {
        console.warn("bootstrapMultiVehicleLocate", e);
      } finally {
        this._multiBootstrapInFlight = false;
      }
    },
    /** 多车查询后切标签/单车多车切换：从 bicycleRunRoute 恢复左侧结果列表 */
    syncLeftJoinMultiVehicleResultIfNeeded() {
      if (this.lastQueryVehicleMode !== "multi") return;
      const lj = this.$refs.leftJoin;
      if (!lj || typeof lj.setMultiVehicleQueryResult !== "function") return;
      const rows = Array.isArray(this.bicycleRunRoute) ? this.bicycleRunRoute : [];
      if (!rows.length) return;
      const existing = Array.isArray(lj.multiVehicleResultList)
        ? lj.multiVehicleResultList
        : [];
      if (existing.length) return;
      lj.setMultiVehicleQueryResult(rows);
    },
    onKeydown(e) {
      if (e.key === "Escape" && this.isMapFullscreen) {
        this.isMapFullscreen = false;
      }
    },
    handleToggle(collapsed) {
      this.isCollapsed = collapsed;
    },
    handleCollapse(num) {
      this.isExpandedNum = num;
      this.$nextTick(() => {
        try {
          if (this.map) this.map.resize();
        } catch (e) {
          /* ignore */
        }
      });
    },
    toggleMapFullscreen() {
      this.isMapFullscreen = !this.isMapFullscreen;
    },
    zoomIn() {
      if (this.map && this.map.getZoom() < 20)
        this.map.setZoom(this.map.getZoom() + 1);
    },
    zoomOut() {
      if (this.map && this.map.getZoom() > 3)
        this.map.setZoom(this.map.getZoom() - 1);
    },
    async zoomGlobal() {
      const mapOk = await this.ensureMapReady();
      if (!mapOk || !this.map) {
        if (!this.mapInitFailed) this.$message.warning("地图未就绪");
        return;
      }
      const points = this.collectLocatePointsGcj();
      if (!points.length) {
        this.$message.warning("暂无定位点可展示");
        return;
      }
      this.fitMapToPoints(points, { saveAsGlobal: true });
    },
    toggleSatelliteLayer() {
      if (!this.map || typeof AMap === "undefined" || !AMap.TileLayer) return;
      if (this.showSatellite) {
        if (this.tileLayer.satellite) this.tileLayer.satellite.setMap(null);
        if (this.tileLayer.roadNet) this.tileLayer.roadNet.setMap(null);
        this.showSatellite = false;
        return;
      }
      if (!this.tileLayer.satellite) {
        this.tileLayer.satellite = new AMap.TileLayer.Satellite();
      }
      if (!this.tileLayer.roadNet) {
        this.tileLayer.roadNet = new AMap.TileLayer.RoadNet();
      }
      this.tileLayer.satellite.setMap(this.map);
      this.tileLayer.roadNet.setMap(this.map);
      this.showSatellite = true;
    },
    toggleRanging() {
      if (!this.map || typeof AMap === "undefined") return;
      if (!this.isRanging) {
        AMap.plugin("AMap.RangingTool", () => {
          this.ruler = new AMap.RangingTool(this.map);
          this.ruler.turnOn();
          this.isRanging = true;
        });
      } else {
        try {
          if (this.ruler) this.ruler.turnOff();
        } catch (e) {
          /* ignore */
        }
        this.isRanging = false;
      }
    },
    /**
     * 初始化地图（可多次调用，返回同一 Promise，避免重复加载 SDK）
     */
    initMap() {
      if (this.mapInitPromise) {
        return this.mapInitPromise;
      }
      this.mapInitFailed = false;
      this.mapInitPromise = loadAmapSDK(["AMap.RangingTool"])
        .then(() => {
          this.createMap();
        })
        .catch((e) => {
          console.error(e);
          this.mapInitFailed = true;
          this.$message.error("地图加载失败");
          throw e;
        });
      return this.mapInitPromise;
    },
    /** 查询/打点前等待地图实例就绪 */
    async ensureMapReady() {
      if (this.map) return true;
      try {
        if (!this.mapInitPromise) {
          this.mapInitPromise = this.initMap();
        }
        await this.mapInitPromise;
      } catch (e) {
        return false;
      }
      return !!this.map;
    },
    createMap() {
      if (this.map) return;
      const host =
        (this.$refs && this.$refs.bicycleMapEl) ||
        document.getElementById("amap-bicycle");
      if (!host) return;
      this.map = new AMap.Map(host, {
        zoom: 11,
        center: [116.404, 39.915],
        resizeEnable: true,
        animateEnable: false,
        viewMode: "2D",
        mapStyle: "amap://styles/normal",
        zooms: [3, 20],
        features: ["bg", "point", "road", "building", "boundary"],
        showLabel: true,
      });
      // 不使用 AMap.ToolBar，避免右下角自带缩放「+」等控件（页面右上角已有缩放按钮）
    },
    clearBicycleMarkers() {
      this.bicycleMarkers.forEach((m) => {
        try {
          m.setMap(null);
        } catch (e) {
          /* ignore */
        }
      });
      this.bicycleMarkers = [];
      this.lastGlobalFitView = null;
      if (this.activeInfoWindow) {
        try {
          this.activeInfoWindow.close();
        } catch (e) {
          /* ignore */
        }
        this.activeInfoWindow = null;
      }
    },
    /**
     * 左侧单车「结果」：传入 getTruckLastLocation 完整 data，由 LeftJoinBicycle 统一取 firstVcl 展示。
     */
    syncLeftJoinBicycleRouteSummary(data, searchForm) {
      const lj = this.$refs.leftJoin;
      if (!lj || typeof lj.updateRouteData !== "function") return;
      if (searchForm.vehicleMode === "multi") {
        lj.updateRouteData(null);
        return;
      }
      const first = data && data.firstVcl ? data.firstVcl : data;
      if (!first || typeof first !== "object") {
        lj.updateRouteData(data);
        return;
      }
      const normalized = this.normalizeLastLocationRow(
        first,
        searchForm && searchForm.vco ? searchForm.vco : "2"
      );
      lj.updateRouteData({
        ...first,
        ...normalized,
      });
    },
    /** 根据左侧搜索面板与底部数据面板实测尺寸计算 fit 边距 [top, right, bottom, left] */
    getMapFitPadding() {
      if (this.isMapFullscreen) {
        return [48, 48, 48, 48];
      }

      const mapEl =
        (this.$refs && this.$refs.bicycleMapEl) ||
        document.getElementById("amap-bicycle");
      const mapRect =
        mapEl && typeof mapEl.getBoundingClientRect === "function"
          ? mapEl.getBoundingClientRect()
          : null;

      let topPad = 72;
      let rightPad = 120;
      let bottomPad = 280;
      let leftPad = this.isCollapsed ? 48 : 400;

      if (mapRect && mapRect.width > 1 && mapRect.height > 1) {
        const lj = this.$refs.leftJoin;
        const leftEl =
          lj && lj.$el ? lj.$el.querySelector(".main-content") : null;
        if (leftEl && !this.isCollapsed) {
          const leftRect = leftEl.getBoundingClientRect();
          if (leftRect.width > 8) {
            leftPad = Math.max(48, Math.ceil(leftRect.right - mapRect.left) + 24);
          }
        }

        if (this.isExpandedNum !== 3) {
          const rd = this.$refs.rightData;
          const bottomEl =
            rd && rd.$refs && rd.$refs.tripInfoPopup
              ? rd.$refs.tripInfoPopup
              : rd && rd.$el
                ? rd.$el.querySelector(".trip-info-popup")
                : null;
          if (bottomEl) {
            const bottomRect = bottomEl.getBoundingClientRect();
            if (bottomRect.height > 8) {
              const overlap = mapRect.bottom - bottomRect.top;
              if (overlap > 0) {
                bottomPad =
                  Math.ceil(overlap) + MARKER_LABEL_CLEARANCE_PX + 20;
              }
            }
          }
        } else {
          bottomPad = 56;
        }

        const mapHost = mapEl && mapEl.parentElement;
        const rightToolsEl =
          mapHost && mapHost.querySelector
            ? mapHost.querySelector(".right-panel")
            : null;
        if (rightToolsEl) {
          const toolsRect = rightToolsEl.getBoundingClientRect();
          if (toolsRect.width > 8) {
            rightPad = Math.max(
              96,
              Math.ceil(mapRect.right - toolsRect.left) + 16
            );
          }
        }
      }

      return [topPad, rightPad, bottomPad, leftPad];
    },
    /** 为车标标签预留地理范围，避免贴边后被底部面板遮挡 */
    expandBoundsForMarkerLabels(sw, ne, pointsGcj) {
      const lngs = pointsGcj.map((p) => p[0]);
      const lats = pointsGcj.map((p) => p[1]);
      const lngSpan = Math.max(...lngs) - Math.min(...lngs);
      const latSpan = Math.max(...lats) - Math.min(...lats);
      const lngBuf = Math.max(lngSpan * 0.06, 0.015);
      const latBufNorth = Math.max(latSpan * 0.05, 0.012);
      const latBufSouth = Math.max(latSpan * 0.1, 0.025);
      return {
        sw: new AMap.LngLat(
          Math.min(...lngs) - lngBuf,
          Math.min(...lats) - latBufSouth
        ),
        ne: new AMap.LngLat(
          Math.max(...lngs) + lngBuf,
          Math.max(...lats) + latBufNorth
        ),
      };
    },
    collectLocatePointsGcj() {
      const rows = Array.isArray(this.bicycleRunRoute) ? this.bicycleRunRoute : [];
      return rows
        .map((r) => lonLatToGcj02(r.lon, r.lat))
        .filter(
          (p) =>
            Array.isArray(p) &&
            p.length === 2 &&
            Number.isFinite(p[0]) &&
            Number.isFinite(p[1])
        );
    },
    saveGlobalFitView() {
      if (!this.map) return;
      try {
        const center = this.map.getCenter();
        const zoom = this.map.getZoom();
        if (center && Number.isFinite(zoom)) {
          this.lastGlobalFitView = {
            center: [center.getLng(), center.getLat()],
            zoom,
          };
        }
      } catch (e) {
        /* ignore */
      }
    },
    fitMapToPoints(pointsGcj, options = {}) {
      const { saveAsGlobal = false } = options;
      if (!this.map || !pointsGcj.length) return;
      const pad = this.getMapFitPadding();
      if (pointsGcj.length === 1) {
        if (typeof this.map.setZoomAndCenter === "function") {
          this.map.setZoomAndCenter(FIT_SINGLE_VEHICLE_ZOOM, pointsGcj[0], true);
        } else {
          this.map.setZoom(FIT_SINGLE_VEHICLE_ZOOM);
          this.map.setCenter(pointsGcj[0]);
        }
        if (saveAsGlobal) {
          this.$nextTick(() => this.saveGlobalFitView());
        }
        return;
      }
      const lngs = pointsGcj.map((p) => p[0]);
      const lats = pointsGcj.map((p) => p[1]);
      const rawSw = new AMap.LngLat(Math.min(...lngs), Math.min(...lats));
      const rawNe = new AMap.LngLat(Math.max(...lngs), Math.max(...lats));
      const { sw, ne } = this.expandBoundsForMarkerLabels(rawSw, rawNe, pointsGcj);
      const applyFit = () => {
        try {
          this.map.setBounds(
            new AMap.Bounds(sw, ne),
            true,
            pad,
            FIT_MULTI_MAX_ZOOM
          );
        } catch (e) {
          this.map.setBounds(new AMap.Bounds(sw, ne), true, pad);
        }
      };
      applyFit();
      const finalize = () => {
        try {
          const z = this.map.getZoom();
          if (z > FIT_MULTI_MAX_ZOOM) this.map.setZoom(FIT_MULTI_MAX_ZOOM);
        } catch (e2) {
          /* ignore */
        }
        if (saveAsGlobal) this.saveGlobalFitView();
      };
      if (saveAsGlobal && typeof this.map.on === "function") {
        let done = false;
        const finalizeOnce = () => {
          if (done) return;
          done = true;
          try {
            this.map.off("moveend", onMoveEnd);
          } catch (e3) {
            /* ignore */
          }
          finalize();
        };
        const onMoveEnd = () => finalizeOnce();
        this.map.on("moveend", onMoveEnd);
        setTimeout(finalizeOnce, 400);
      } else {
        this.$nextTick(finalize);
      }
    },
    async handleFocusRightTableRow(row) {
      const mapOk = await this.ensureMapReady();
      if (!mapOk || !this.map) {
        if (!this.mapInitFailed) this.$message.warning("地图未就绪");
        return;
      }
      const payload =
        row && row._raw && typeof row._raw === "object" ? row._raw : row;
      const gcj =
        row && row._gcj && typeof row._gcj === "object" ? row._gcj : null;
      let lng = gcj && Number.isFinite(Number(gcj.lng)) ? Number(gcj.lng) : NaN;
      let lat = gcj && Number.isFinite(Number(gcj.lat)) ? Number(gcj.lat) : NaN;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        if (!payload || payload.lon == null || payload.lat == null) return;
        const t = lonLatToGcj02(payload.lon, payload.lat);
        lng = t[0];
        lat = t[1];
      }
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      await this.focusMapAndOpenInfo(lng, lat, payload, {
        zoom: 13,
        aboveMarker: true,
      });
    },
    async handleHistoryLocateOnMap(item) {
      const mapOk = await this.ensureMapReady();
      if (!mapOk || !this.map) {
        if (!this.mapInitFailed) this.$message.warning("地图未就绪");
        return;
      }
      const row = item && typeof item === "object" ? item : null;
      if (!row || row.lon == null || row.lat == null) {
        this.$message.warning("该条历史无有效经纬度");
        return;
      }
      const [lng, lat] = lonLatToGcj02(row.lon, row.lat);
      let z = this.map.getZoom();
      z = Math.max(8, Math.min(z, 13));
      await this.focusMapAndOpenInfo(lng, lat, row, {
        zoom: z,
        aboveMarker: false,
      });
    },
    async handleResultLocateOnMap(item) {
      const mapOk = await this.ensureMapReady();
      if (!mapOk || !this.map) {
        if (!this.mapInitFailed) this.$message.warning("地图未就绪");
        return;
      }
      const row = item && typeof item === "object" ? item : null;
      if (!row || row.lon == null || row.lat == null) return;
      const [lng, lat] = lonLatToGcj02(row.lon, row.lat);
      await this.focusMapAndOpenInfo(
        lng,
        lat,
        row.raw && typeof row.raw === "object" ? row.raw : row,
        { zoom: 13, aboveMarker: true }
      );
    },
    /** 打开 info 后做一次 panBy：把目标点移到可视中心 */
    panMapToKeepInfoCentered(lng, lat, opts) {
      try {
        if (!this.map || typeof this.map.panBy !== "function") return;
        if (!Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat)))
          return;
        const options = opts && typeof opts === "object" ? opts : {};
        const aboveMarker = options.aboveMarker !== false;

        const mapEl =
          this.map && this.map.getContainer ? this.map.getContainer() : null;
        const w = mapEl && mapEl.clientWidth ? mapEl.clientWidth : 1200;
        const h = mapEl && mapEl.clientHeight ? mapEl.clientHeight : 800;

        let leftW = 0;
        if (!this.isCollapsed) {
          const leftEl = document.querySelector(".leftjoin-root");
          if (leftEl && leftEl.offsetWidth) leftW = leftEl.offsetWidth;
        }

        // “可视区域中心”：左侧面板覆盖地图时，中心点右移到剩余区域的中心
        const targetX = this.isCollapsed ? w / 2 : leftW + (w - leftW) / 2;
        // 信息窗在点上方时，让点稍微偏下，保证信息窗也在可视范围内
        const targetY = aboveMarker ? h * 0.62 : h * 0.55;

        const cur = this.map.lngLatToContainer([Number(lng), Number(lat)]);
        if (!cur || !Number.isFinite(cur.x) || !Number.isFinite(cur.y)) return;

        // panBy 移动的是“地图内容”，点的屏幕位置会反向移动，所以取 (cur - target)
        const dx = Math.round(cur.x - targetX);
        const dy = Math.round(cur.y - targetY);
        if (dx === 0 && dy === 0) return;
        this.map.panBy(dx, dy);
      } catch (e) {
        /* ignore */
      }
    },

    async focusMapAndOpenInfo(lng, lat, row, opts) {
      const options = opts && typeof opts === "object" ? opts : {};
      const zoom = options.zoom != null ? Number(options.zoom) : null;
      const aboveMarker = options.aboveMarker !== false;
      if (!this.map) return;
      try {
        this.map.setCenter([lng, lat]);
        if (Number.isFinite(zoom)) this.map.setZoom(zoom);
      } catch (e) {
        /* ignore */
      }
      await this.$nextTick();
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      this.openVehicleInfo(lng, lat, row, { aboveMarker });
    },
    openVehicleInfo(lng, lat, row, opts) {
      const options = opts && typeof opts === "object" ? opts : {};
      const aboveMarker = options.aboveMarker !== false;
      if (!this.map || typeof AMap === "undefined") return;
      if (this.activeInfoWindow) {
        try {
          this.activeInfoWindow.close();
        } catch (e) {
          /* ignore */
        }
        this.activeInfoWindow = null;
      }
      const plate = row.vclN || row.vno || "—";
      const spd = parseSpd(row);
      const dir = formatDirectionText(row);
      const timeStr = formatIwUpdateTime(row);
      const statusText = formatBicycleTableStatus(row);
      const mileageText = formatIwMileageKm(row);
      const lngStr = Number.isFinite(Number(lng))
        ? Number(lng).toFixed(6)
        : "—";
      const latStr = Number.isFinite(Number(lat))
        ? Number(lat).toFixed(6)
        : "—";
      const spdStr = spd == null ? '未知' : `${spd.toFixed(spd % 1 === 0 ? 0 : 1)}km/h`;
      const addrPlaceholder = row.adr || row.location || "解析中...";
      const labelColor = "#606266";
      const valueColor = "#303133";
      const statusColor =
        statusText === "离线"
          ? "#909399"
          : statusText === "停止"
          ? "#f56c6c"
          : statusText === "在线"
          ? "#52c41a"
          : "#909399";

      const root = document.createElement("div");
      root.style.cssText =
        "display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 2px 8px rgba(0,0,0,.12))";
      const card = document.createElement("div");
      card.style.cssText =
        "position:relative;width:300px;box-sizing:border-box;padding:12px 16px 12px;background:#fff;border-radius:10px;font-size:13px;color:#303133;line-height:1.55";

      const closeBtn = document.createElement("span");
      closeBtn.setAttribute("role", "button");
      closeBtn.setAttribute("aria-label", "关闭");
      closeBtn.textContent = "×";
      closeBtn.style.cssText =
        "position:absolute;right:10px;top:10px;z-index:1;cursor:pointer;font-size:20px;line-height:1;color:#c0c4cc;user-select:none";
      closeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (this.activeInfoWindow) {
          try {
            this.activeInfoWindow.close();
          } catch (err) {
            /* ignore */
          }
          this.activeInfoWindow = null;
        }
      });
      card.appendChild(closeBtn);

      const header = document.createElement("div");
      header.style.cssText = "padding:2px 28px 0 0";
      const topRow = document.createElement("div");
      topRow.style.cssText =
        "display:flex;justify-content:space-between;align-items:flex-start;gap:8px";
      const leftTitle = document.createElement("div");
      leftTitle.style.cssText = "flex:1;min-width:0;word-break:break-all";
      const plateSpan = document.createElement("span");
      plateSpan.style.cssText = "font-size:16px;font-weight:700;color:#303133";
      plateSpan.textContent = plate;
      leftTitle.appendChild(plateSpan);
      const statusEl = document.createElement("span");
      statusEl.style.cssText = `flex-shrink:0;font-size:14px;font-weight:600;color:${statusColor};white-space:nowrap;padding-top:2px`;
      statusEl.textContent =
        statusText === "离线" || statusText === "停止" || statusText === "在线"
          ? statusText
          : "—";
      topRow.appendChild(leftTitle);
      topRow.appendChild(statusEl);
      header.appendChild(topRow);
      card.appendChild(header);
      const hr1 = document.createElement("div");
      hr1.style.cssText = "border:0;border-top:1px solid #ebeef5;margin:0";
      card.appendChild(hr1);

      const body = document.createElement("div");
      body.style.cssText = "font-size:12px;color:#606266;padding:10px 0 0";
      const addLine = (labelText, valueText) => {
        const line = document.createElement("div");
        const lab = document.createElement("span");
        lab.style.color = labelColor;
        lab.textContent = labelText;
        const val = document.createElement("span");
        val.style.color = valueColor;
        val.textContent = valueText;
        line.appendChild(lab);
        line.appendChild(val);
        body.appendChild(line);
      };
      addLine("速度：", spdStr);
      addLine("方向：", dir);
      addLine("累计里程：", mileageText);

      const llRow = document.createElement("div");
      llRow.style.cssText =
        "font-size:12px;line-height:1.5;word-break:break-all";
      const lonLab = document.createElement("span");
      lonLab.style.color = labelColor;
      lonLab.textContent = "经度：";
      const lonVal = document.createElement("span");
      lonVal.style.color = valueColor;
      lonVal.textContent = lngStr;
      const midSp = document.createElement("span");
      midSp.textContent = "\u00A0\u00A0\u00A0\u00A0";
      const latLab = document.createElement("span");
      latLab.style.color = labelColor;
      latLab.textContent = "纬度：";
      const latVal = document.createElement("span");
      latVal.style.color = valueColor;
      latVal.textContent = latStr;
      llRow.appendChild(lonLab);
      llRow.appendChild(lonVal);
      llRow.appendChild(midSp);
      llRow.appendChild(latLab);
      llRow.appendChild(latVal);
      body.appendChild(llRow);

      const navTargetName =
        addrPlaceholder && addrPlaceholder !== "解析中..."
          ? String(addrPlaceholder).trim()
          : "目的地";
      const navUrl =
        Number.isFinite(Number(lng)) && Number.isFinite(Number(lat))
          ? `https://uri.amap.com/navigation?to=${Number(lng).toFixed(6)},${Number(lat).toFixed(6)},${encodeURIComponent(navTargetName)}`
          : "";
      const addrLine = document.createElement("div");
      const addrLab = document.createElement("span");
      addrLab.style.color = labelColor;
      addrLab.textContent = "当前位置：";
      const addrSpan = document.createElement("span");
      addrSpan.className = "bicycle-iw-addr";
      addrSpan.style.cssText = "color:#1677ff;word-break:break-all";
      addrSpan.textContent = addrPlaceholder;
      addrLine.appendChild(addrLab);
      addrLine.appendChild(addrSpan);
      if (navUrl) {
        const navLink = document.createElement("a");
        navLink.href = navUrl;
        navLink.target = "_blank";
        navLink.rel = "noopener noreferrer";
        navLink.textContent = "导航";
        navLink.style.cssText =
          "display:inline-flex;align-items:center;justify-content:center;margin-left:8px;padding:1px 6px;border-radius:4px;background:#1677ff;color:#fff;text-decoration:none;font-size:12px;line-height:1.5;white-space:nowrap";
        navLink.addEventListener("click", (e) => e.stopPropagation());
        addrLine.appendChild(navLink);
      }
      body.appendChild(addrLine);
      addLine("更新时间：", timeStr);
      card.appendChild(body);

      root.appendChild(card);
      const pointer = document.createElement("div");
      pointer.setAttribute("aria-hidden", "true");
      pointer.style.cssText =
        "width:0;height:0;margin-top:-1px;border-left:9px solid transparent;border-right:9px solid transparent;border-top:10px solid #fff;flex-shrink:0";
      root.appendChild(pointer);

      this.activeInfoWindow = new AMap.InfoWindow({
        isCustom: true,
        content: root,
        anchor: "bottom-center",
        offset: new AMap.Pixel(0, aboveMarker ? -(32 / 2 + 4) : -10),
        // 交给外层做“视觉居中”，避免 autoMove 与 panBy 打架
        autoMove: false,
      });
      this.activeInfoWindow.open(this.map, [lng, lat]);

      if (!row.adr && !row.location) {
        tiandituReverseGeocode(lng, lat).then((r) => {
          const el = root.querySelector(".bicycle-iw-addr");
          if (el && r.success && r.des) el.textContent = r.des;
        });
      }
    },
    goTransportReplay(vehicleRow) { if (!hasPermission('truck:track')) return this.$message.warning('没有货车轨迹权限'); this.$router.push({ path: '/vehicle-monitor/tracks', query: { vclN: vehicleRow.vclN || vehicleRow.vno, vco: String(vehicleRow.vco || '') } }) },
    /**
     * @param {Array} pointsRaw
     * @param {{ mapFocusMode?: 'all'|'lastChecked', focusVclN?: string }} [options]
     *  - all / 默认：适配全部 marker（查询按钮）
     *  - lastChecked：全量打点后定位到 focusVclN 并打开 info（车库勾选）
     */
    renderBicycleMarkers(pointsRaw, options = {}) {
      this.clearBicycleMarkers();
      if (!this.map || typeof AMap === "undefined") return;
      const positions = [];
      const list = Array.isArray(pointsRaw) ? pointsRaw : [];
      list.forEach((row, idx) => {
        const [lng, lat] = lonLatToGcj02(row.lon, row.lat);
        positions.push([lng, lat]);
        const iconUrl = pickBicycleMarkerIconUrl(row);
        const { root, plate, spdStr } = buildBicycleMarkerContent(row, iconUrl);
        const marker = new AMap.Marker({
          position: [lng, lat],
          title: `${plate} ${spdStr}`,
          anchor: "center",
          content: root,
          zIndex: 100 + idx,
        });
        marker.on("click", () => {
          this.focusMapAndOpenInfo(lng, lat, row, {
            zoom: null,
            aboveMarker: true,
          });
        });
        marker.setMap(this.map);
        this.bicycleMarkers.push(marker);
      });
      const focusPlate =
        options &&
        options.mapFocusMode === "lastChecked" &&
        options.focusKey
          ? String(options.focusKey).trim()
          : "";
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (focusPlate) {
            const target = list.find(
              (r) => r && identityKey(r) === focusPlate
            );
            if (target && target.lon != null && target.lat != null) {
              const [lng, lat] = lonLatToGcj02(target.lon, target.lat);
              this.focusMapAndOpenInfo(lng, lat, target, {
                zoom: 13,
                aboveMarker: true,
              });
              return;
            }
          }
          this.fitMapToPoints(positions, { saveAsGlobal: true });
        });
      });
    },
    /**
     * /webserver/getTruckLastLocation：vnos=车牌_颜色 必填；坐标为高德选点(GCJ02)时转为 WGS84 再传 optional 字段
     * 多车：仅传 vnos，如 鲁Q123GB_2,粤BBA885_3
     */
    buildGetTruckLastLocationFormData(searchForm) {
      if (
        searchForm.vehicleMode === "multi" &&
        searchForm.vnos != null &&
        String(searchForm.vnos).trim() !== ""
      ) {
        const fd = new FormData();
        fd.append("vnos", String(searchForm.vnos).trim());
        return fd;
      }
      const fd = new FormData();
      const plate = String(searchForm.vclN || "").trim();
      const vco = String(
        searchForm.vco != null && searchForm.vco !== "" ? searchForm.vco : "2"
      );
      fd.append("vnos", `${plate}_${vco}`);

      const gcjStrToWgsLonLatStr = (coordStr) => {
        if (coordStr == null || String(coordStr).trim() === "") return "";
        const parts = String(coordStr).trim().split(',').map(value => value.trim() === '' ? NaN : Number(value));
        if (parts.length !== 2 || parts.some(value => !Number.isFinite(value)) || Math.abs(parts[0]) > 180 || Math.abs(parts[1]) > 90) throw new Error('请填写有效的起终点坐标');
        const [lng, lat] = parts;
        const [wLon, wLat] = coordtransform.gcj02towgs84(lng, lat);
        const lonOut = Number(wLon);
        const latOut = Number(wLat);
        if (!Number.isFinite(lonOut) || !Number.isFinite(latOut)) return "";
        return `${lonOut.toFixed(6)},${latOut.toFixed(6)}`;
      };
      const sl = gcjStrToWgsLonLatStr(searchForm.startLonlat);
      if (sl) fd.append("startLonlat", sl);
      const el = gcjStrToWgsLonLatStr(searchForm.endLonlat);
      if (el) fd.append("endLonlat", el);
      const sac = searchForm.startAreaCode;
      if (sac != null && String(sac).trim() !== "")
        fd.append("startAreaCode", String(sac).trim());
      const eac = searchForm.endAreaCode;
      if (eac != null && String(eac).trim() !== "")
        fd.append("endAreaCode", String(eac).trim());
      return fd;
    },
    /** 接口 firstVcl / others 项 → 地图与信息窗用行（lon/lat 与扩展字段，内容语义不改） */
    normalizeLastLocationRow(apiV, fallbackVco) {
      const v = apiV && typeof apiV === "object" ? apiV : {};
      const gtm = utcToGtmFromApi(v.utc);
      let sts =
        v.sts != null && v.sts !== ""
          ? String(v.sts)
          : getBicycleMarkerState(v);
      return {
        lon: v.lon,
        lat: v.lat,
        vclN: v.vno || v.vclN || "—",
        vco:
          v.vco != null && v.vco !== ""
            ? String(v.vco)
            : String(
                fallbackVco != null && fallbackVco !== "" ? fallbackVco : "2"
              ),
        adr: v.adr || v.address,
        location: v.adr || v.address,
        offlineTime: v.offlineTime,
        gtm: gtm || undefined,
        spd: v.spd,
        rawSpeed: v.rawSpeed, key: identityKey(v),
        drc:
          v.drc != null && v.drc !== ""
            ? v.drc
            : v.head != null && v.head !== ""
              ? v.head
              : undefined,
        head: v.head,
        agl: v.agl != null && v.agl !== "" ? v.agl : v.head,
        mil: v.mil,
        province: v.province,
        city: v.city,
        status: v.status,
        online: sts === "在线",
        sts,
        raw: { ...v },
      };
    },
    /**  unwrap axios 包裹的 data */
    unwrapTruckLastLocationRes(res) {
      let data = res && res.data != null ? res.data : null;
      if (data && typeof data === "object" && data.data != null) {
        data = data.data;
      }
      return data;
    },
    /**
     * 兼容接口把首车写成 firstVol 的情况，统一为 firstVcl
     */
    normalizeGetTruckLastLocationData(data) {
      if (!data || typeof data !== "object") return data;
      const out = { ...data };
      if (out.firstVcl == null && out.firstVol != null) {
        out.firstVcl = out.firstVol;
      }
      return out;
    },
    /** 多车 vnos 字符串 → 与接口返回顺序对齐颜色回退用 */
    parseVnosSegments(vnosStr) {
      const out = [];
      for (const seg of String(vnosStr || "").split(",")) {
        const s = seg.trim();
        if (!s) continue;
        const last = s.lastIndexOf("_");
        if (last <= 0) {
          out.push({ vclN: s, vco: "2" });
        } else {
          out.push({ vclN: s.slice(0, last), vco: s.slice(last + 1) || "2" });
        }
      }
      return out;
    },
    async handleSearch(searchForm) {
 try {
  if (!hasPermission('truck:position')) throw new Error('没有货车定位权限');
  if (searchForm.vehicleMode === 'multi' && !String(searchForm.vnos || '').trim()) { this.positionSession.cancel(); this.positionSession.items = []; this.syncPositionRows(); return }
  const identities = queryIdentities(searchForm);
  this.lastQueryVehicleMode = searchForm.vehicleMode === 'multi' ? 'multi' : 'single';
  this.positionSearchForm = { ...searchForm };
  const fields = this.buildGetTruckLastLocationFormData(searchForm);
  this.positionConditions = Object.fromEntries(fields.entries());
  this.clearBicycleMarkers(); this.bicycleRunRoute = [];
  await this.positionSession.start(identities);
  this.syncPositionRows();
 } catch (error) { this.$message.error(error.msg || error.message || '定位查询失败') }
},
  },
};
</script>
<style scoped>
.native-position-status { position: absolute; top: 8.5rem; right: 1rem; z-index: 15; background: white; padding: .5rem; max-height: 14rem; overflow: auto; font-size: 12px; }
.el-input__icon{
  line-height: 30px!important;
}
</style>
<style scoped>
.bicycle-plugins-container {
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
}

.map-container {
  left: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  z-index: 1;
}

.right-panel {
  flex: 3;
  border-radius: 4px;
  padding: 15px;
  position: absolute;
  z-index: 9;
  //width: 11.5rem;
  display: flex;
  flex-direction: column;
  justify-content: start;
  right: 0;
  top: 0;
  pointer-events: none;
}

.top-controls {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.625rem;
  pointer-events: none;
}

.top-controls__map-tools,
.zoom-controls {
  pointer-events: auto;
}

.top-controls__map-tools {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  gap: 0.5rem;
  border-radius: 0.3125rem;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.12);
  padding: 0.6rem 0.75rem !important;
}

.map-tool-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
}

.map-tool-item:hover {
  background: rgba(22, 119, 255, 0.06);
}

.control-btn-icon {
  width: 1.125rem;
  height: 1.125rem;
  display: block;
}

.control-btn {
  font-size: 0.875rem;
  border: none;
  background: transparent;
  padding: 0;
  line-height: 1.125rem;
  color: #303133;
}

.zoom-controls {
  position: relative;
  background: #fff;
  display: flex;
  align-items: center;
  border-radius: 0.3125rem;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.12);
  padding: 0.6rem 0.75rem !important;
  gap: 0.5rem;
}

.zoom-controls i {
  font-size: 1.5rem;
  font-weight: 700;
}

.amap {
  width: 100%;
  height: 100%;
}

.fullscreen-tip {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 10000;
}

.esc-key {
  font-weight: bold;
  color: #409eff;
}

.map-fullscreen .map-container {
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw !important;
  height: 100vh !important;
}

@media (max-width: 768px) {
  .right-panel {
    width: auto;
    max-width: calc(100vw - 1rem);
    padding: 0.5rem;
    pointer-events: none;
  }

  .top-controls {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 0.375rem;
    pointer-events: none;
  }

  .top-controls__map-tools,
  .zoom-controls {
    pointer-events: auto;
  }

  .top-controls__map-tools {
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem !important;
  }

  .map-tool-item .control-btn {
    display: none;
  }

  .zoom-controls {
    padding: 0.375rem 0.5rem !important;
  }

  .zoom-controls i {
    font-size: 1.25rem;
  }
}
</style>

<style>
#amap-bicycle .amap-info-content {
  box-sizing: border-box;
  border-radius: 0.48rem;
}

.bicycle-plugins-container.map-fullscreen .leftjoin-root,
.bicycle-plugins-container.map-fullscreen .right-data-bicycle-root,
.bicycle-plugins-container.map-fullscreen .right-panel {
  display: none !important;
}
</style>
